import { useEffect, useState, useCallback } from "react";
import { getAlerts, exportAlerts } from "@/lib/procureApi";
import { STAGE, fmtInt, fmtDate } from "@/lib/format";
import { WarningOctagon, ArrowSquareOut, DownloadSimple, Spinner, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const StageBadge = ({ t }) => {
  const s = STAGE[t] || { color: "#64748B" };
  return (
    <span className="inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[11px] font-semibold" style={{ background: `${s.color}22`, color: s.color }}>
      {t}
    </span>
  );
};

const ThresholdInput = ({ stage, value, onChange }) => {
  const s = STAGE[stage];
  return (
    <label className="flex items-center gap-1.5">
      <span className="inline-block h-2 w-2" style={{ background: s.color }} />
      <span className="pf-label">{stage}</span>
      <input
        type="number"
        min={0}
        data-testid={`alert-threshold-${stage}`}
        value={value}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
        className="h-8 w-14 rounded-sm border border-[#E3E7EC] bg-[#F1F3F6] px-2 text-center font-mono text-sm text-[#0F172A] outline-none focus:border-[#F0323E]"
      />
      <span className="text-[11px] text-[#9CA3AF]">d</span>
    </label>
  );
};

export const StuckAlerts = ({ snapshotId, filters, onOpenRef2, refreshKey }) => {
  const [thr, setThr] = useState({ or_days: 14, oq_days: 21, of_days: 7 });
  const [data, setData] = useState({ alerts: [], total: 0, by_stage: {} });
  const [loaded, setLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      await exportAlerts({ snapshot_id: snapshotId, ...thr, buyer: filters?.buyer, branch: filters?.branch });
      toast.success(`Exported ${fmtInt(data.total)} stuck alert(s) to Excel`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(async () => {
    const res = await getAlerts({
      snapshot_id: snapshotId,
      ...thr,
      buyer: filters?.buyer,
      branch: filters?.branch,
    });
    setData(res);
    setLoaded(true);
  }, [snapshotId, thr, filters?.buyer, filters?.branch, refreshKey]);

  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  return (
    <div className="col-span-12 border border-[#E3E7EC] bg-[#FFFFFF]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E3E7EC] p-4">
        <div className="flex items-center gap-2">
          <WarningOctagon size={18} weight="duotone" className="text-[#F0323E]" />
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A]">
              Stuck Order Alerts
              <span className="ml-2 rounded-sm bg-[#F0323E] px-2 py-0.5 font-mono text-xs text-white" data-testid="alerts-total">
                {loaded ? fmtInt(data.total) : "…"}
              </span>
            </h3>
            <p className="pf-label mt-0.5">
              REF2 groups past threshold per stage · OR {data.by_stage?.OR ?? 0} · OQ {data.by_stage?.OQ ?? 0} · OF {data.by_stage?.OF ?? 0}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <span className="pf-label">Thresholds</span>
          <ThresholdInput stage="OR" value={thr.or_days} onChange={(v) => setThr((t) => ({ ...t, or_days: v }))} />
          <ThresholdInput stage="OQ" value={thr.oq_days} onChange={(v) => setThr((t) => ({ ...t, oq_days: v }))} />
          <ThresholdInput stage="OF" value={thr.of_days} onChange={(v) => setThr((t) => ({ ...t, of_days: v }))} />
          <button
            data-testid="alerts-export"
            onClick={doExport}
            disabled={exporting || data.total === 0}
            title="Export these stuck alerts to Excel"
            className="flex h-9 items-center gap-2 rounded-sm border border-[#CAD1DB] bg-white px-3 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-[#EDEFF3] disabled:opacity-50"
          >
            {exporting ? <Spinner size={15} className="animate-spin" /> : <DownloadSimple size={15} weight="bold" />}
            Excel
          </button>
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full border-collapse text-sm" data-testid="alerts-table">
          <thead className="sticky top-0 z-10 bg-[#FFFFFF]">
            <tr className="border-b border-[#E3E7EC] text-left">
              {["Stage", "Ref 2", "Description", "Buyer", "Branch", "Age", "Over By", "Lines"].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-2.5 pf-label">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.alerts.map((a, i) => (
              <tr
                key={`${a.reference2}-${a.stage}-${i}`}
                data-testid={`alert-row-${i}`}
                onClick={() => onOpenRef2(a.reference2)}
                data-critical={a.critical_lines ? "true" : "false"}
                className={`group cursor-pointer border-b border-[#E3E7EC]/50 transition-colors ${
                  a.critical_lines ? "bg-[#FDE2E4] font-bold [&_td]:!text-[#B91C1C] hover:bg-[#FBCFD3]" : "hover:bg-[#EDEFF3]"
                }`}
              >
                <td className="px-4 py-2"><StageBadge t={a.stage} /></td>
                <td className="whitespace-nowrap px-4 py-2 font-mono text-[#F0323E]">
                  <span className="inline-flex items-center gap-1 group-hover:underline">
                    {a.reference2}
                    <ArrowSquareOut size={12} className="opacity-0 group-hover:opacity-100" />
                  </span>
                </td>
                <td className="max-w-[260px] truncate px-4 py-2 text-[#0F172A]">{a.description || "—"}</td>
                <td className="whitespace-nowrap px-4 py-2 text-[#64748B]">{a.buyer}</td>
                <td className="whitespace-nowrap px-4 py-2 text-[#64748B]">{a.branch || "—"}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono font-semibold text-red-600">{a.max_age}d</td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-amber-600">+{a.over_by}d</td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-[#64748B]">
                  <span className="inline-flex items-center gap-1">
                    {a.critical_lines > 0 && <span title="Production Driver lines" className="inline-flex items-center gap-0.5"><Lightning size={11} weight="fill" />{a.critical_lines}/</span>}
                    {a.lines}
                  </span>
                </td>
              </tr>
            ))}
            {data.alerts.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-emerald-600">No stuck orders past these thresholds.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
